/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PagingGroupsPage.tsx
 🗓️ Created: 2025-01-22
 🧩 Description:
    Main page for Paging Groups management with list view.
    Displays paging groups that broadcast audio to multiple extensions.

 🔧 Notes:
    - Domain-scoped data
    - Supports pagination and search
    - Includes delete confirmation

 🔁 Last Updated: 2025-01-22 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useContextStore } from '../../store/context';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { deletePagingGroupById, navigateToPagingGroupDuplicate } from './pagingGroupsActions';
import { usePagingGroupsList } from './hooks/usePagingGroupsList';

export interface PagingGroup {
  id: number;
  uuid: string;
  domainId: number;
  code: string;
  name: string;
  description?: string;
  context: string;
  codec: string;
  volume: number;
  timeout: number;
  mode: string;
  announcementPath?: string;
  multicastAddress?: string;
  recordEnabled: boolean;
  skipBusy: boolean;
  settings?: Record<string, unknown>;
  enabled: boolean;
  createdAt: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
  members?: PagingGroupMember[];
}

export interface PagingGroupMember {
  id: number;
  pagingGroupId: number;
  extension: string;
  createdAt: string;
  updatedAt?: string;
}

export default function PagingGroupsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; group: PagingGroup | null }>({
    isOpen: false,
    group: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Generate columns
  const availableColumns = useMemo(() => {
    return [
      {
        key: 'code',
        label: t('pagingGroups.code'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: PagingGroup) => (
          <span className="font-mono font-medium text-primary">{item?.code || '-'}</span>
        )
      },
      {
        key: 'name',
        label: t('pagingGroups.name'),
        visible: true,
        sortable: true
      },
      {
        key: 'members',
        label: t('pagingGroups.members'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: PagingGroup) => {
          if (!item || !item.members || item.members.length === 0) return '-';
          return (
            <span className="text-sm">
              {item.members.length} {t('pagingGroups.member')}
            </span>
          );
        }
      },
      {
        key: 'mode',
        label: t('pagingGroups.mode'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: PagingGroup) => (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
            {item?.mode || '-'}
          </span>
        )
      },
      {
        key: 'enabled',
        label: t('pagingGroups.enabled'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: PagingGroup) => (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}>
            {item?.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        )
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false
      }
    ];
  }, [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = usePagingGroupsList({
    initialColumns: availableColumns,
    initialSortBy: 'code',
    initialSortOrder: 'asc',
    initialLimit: 10,
    domainId: selectedDomain?.id,
  });

  // Handlers
  const handleCreate = useCallback(() => {
    navigate('/pbx/applications/paging/new');
  }, [navigate]);

  const handleEdit = useCallback((group: PagingGroup) => {
    navigate(`/pbx/applications/paging/${group.uuid || group.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((group: PagingGroup) => {
    navigateToPagingGroupDuplicate(navigate, String(group.uuid || group.id));
  }, [navigate]);

  const handleDeleteClick = useCallback((group: PagingGroup) => {
    setDeleteModal({ isOpen: true, group });
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteModal.group) return;

    setIsDeleting(true);
    try {
      await deletePagingGroupById(String(deleteModal.group.uuid || deleteModal.group.id));

      showSuccess(t('pagingGroups.messages.deletedSuccess'));
      setDeleteModal({ isOpen: false, group: null });
      refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('pagingGroups.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Render row actions
  const renderRowActions = useCallback((group: PagingGroup) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(group)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(group)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(group)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDeleteClick}
          onColumnToggle={handleColumnToggle}
          title={t('pagingGroups.title')}
          titleIcon={<Megaphone className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          renderActions={renderRowActions}
          emptyMessage={t('pagingGroups.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, group: null })}
        onConfirm={handleDeleteConfirm}
        title={t('pagingGroups.deleteTitle')}
        message={t('pagingGroups.messages.confirmDelete', {
          name: deleteModal.group?.name || ''
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

